<script setup lang="ts">
import type { CustomVars } from '@/components/ui/TextareaWithVars/interfaces'
import type { LoyaltyNotificationsConfigFields } from '@/types/types'

const { t } = useI18n()
const router = useRouter()
const { updateMultiselectStores } = useMerchantNotificationConfigs()
const props = defineProps<{
  loyaltyNotificationsConfigFields: LoyaltyNotificationsConfigFields
}>()

const emit = defineEmits<{
  (e: 'update:loyalty-notification-config', value: LoyaltyNotificationsConfigFields): void
  (e: 'has-message-content-error', value: boolean): void
}>()

updateMultiselectStores('subscription')

const handleFieldUpdate = ($event) => {
  const updatedLoyaltyNotificationsConfigFields = props.loyaltyNotificationsConfigFields
  updatedLoyaltyNotificationsConfigFields.loyaltyDeliveryOrderMessageChannel.value = $event
  emit('update:loyalty-notification-config', updatedLoyaltyNotificationsConfigFields)
}

const filteredLoyaltyNotificationsConfigFields = computed(() => {
  return {
    smsName: props.loyaltyNotificationsConfigFields.smsName,
    hasNotificationAfterOrder: props.loyaltyNotificationsConfigFields.hasNotificationAfterOrder
  }
})

const customVariables = computed<CustomVars[]>(() => [
  {
    type: 'variable',
    code: 'points',
    label: t('global.customVars.points.name'),
    icon: 'HeroGiftIconSolid',
    length: 4
  }
])

const messageContentErrors = ref<Record<string, boolean>>({})

const handleMessageContentError = (index: number, error: boolean) => {
  messageContentErrors.value[index] = error

  if (props.loyaltyNotificationsConfigFields?.loyaltyDeliveryOrderMessageChannel.value?.value === 'sms') {
    emit('has-message-content-error', messageContentErrors.value['integrationAfterOrderSms'])
  } else {
    emit(
      'has-message-content-error',
      messageContentErrors.value['integrationAfterOrderMailBody'] ||
        messageContentErrors.value['integrationAfterOrderMailSubject']
    )
  }
}
</script>

<template>
  <TabSubPanel :section-main-title="t('pages.loyaltyPage.tabs.notifications.sectionTitle')">
    <template #content>
      <BaseTip :text="t('pages.loyaltyPage.tabs.notifications.tip.title')" severity="info">
        <template #button>
          <button
            class="w-max flex items-center text-sm font-medium text-blue-600 cursor-pointer"
            @click="router.push({ name: 'AutopilotList', query: { activeTab: 'templates' } })"
          >
            {{ t('pages.loyaltyPage.tabs.notifications.tip.button') }}
            <BaseIcon name="HeroArrowLongRightIconSolid" class="ml-3 h-5 w-5 shrink-0 text-blue-600" />
          </button>
        </template>
      </BaseTip>
      <div class="mt-5 space-y-5 p-5 sm:rounded-md sm:border sm:border-border">
        <div class="grid grid-cols-2 gap-5">
          <div v-for="(item, key) in filteredLoyaltyNotificationsConfigFields" :key="key">
            <AppFormInput
              v-if="item.type === 'text'"
              :id="item.label"
              v-model="item.value"
              :type="item.type"
              :min-length="item.minLength"
              :max-length="item.maxLength"
              :required="item.required"
              :label="item.label"
              :disabled="item.disabled"
              :tool-tip="item.toolTip"
            />
            <div v-if="item.type === 'toggle'" class="space-y-3">
              <label :for="item.label" class="block text-sm font-medium text-foreground">{{ item.label }}</label>
              <BaseSwitch
                :id="item.label"
                v-model="item.value"
                class="inline-flex"
                :required="item.required"
                :font-color="item.value ? 'green' : 'red'"
                :label="item.value ? t('global.words.enabled') : t('global.words.disabled')"
              />
            </div>
          </div>
        </div>
        <div v-if="loyaltyNotificationsConfigFields.hasNotificationAfterOrder.value">
          <AppRadioCard
            :options="loyaltyNotificationsConfigFields.loyaltyDeliveryOrderMessageChannel.options"
            :model-value="loyaltyNotificationsConfigFields.loyaltyDeliveryOrderMessageChannel.value"
            class="grid grid-cols-2 gap-5"
            @update:model-value="handleFieldUpdate"
          />
          <div class="grid grid-cols-1 gap-5 py-5 lg:grid-cols-2">
            <template v-for="(item, key) in loyaltyNotificationsConfigFields.orderReceivedTextsFields" :key="key">
              <div
                v-if="
                  loyaltyNotificationsConfigFields.loyaltyDeliveryOrderMessageChannel.value?.value ===
                  item.notificationChannel
                "
              >
                <TextareaWithVars
                  v-model="item.value"
                  :label="item.label"
                  :max-length="item.maxLength"
                  :is-disabled="item.disabled"
                  :i18n-strings="{
                    textareaLabel: item.label,
                    characters: t('global.words.characters'),
                    insertVar: t('global.customVars.insertLabel'),
                    errorEmojiInMessage: t('ui.error.strings.unauthorizedEmoji')
                  }"
                  :custom-vars="customVariables"
                  :sanitize-for-gsm="item.notificationChannel === 'sms'"
                  @has-error="handleMessageContentError(key, $event)"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
  </TabSubPanel>
</template>
